export class InvalidVisualAcuity extends Error {
}
/**
 * Some acuities are not fractions at all. They are the standard shorthand for vision too poor to
 * read a chart, and they order below every chart line.
 *
 * The LogMAR values are the conventional research equivalents (Holladay, Schulze-Bonsel). They are
 * approximations by nature: counting fingers is not a chart measurement. They are here so that
 * "aided is worse than unaided" still answers when one side is off the chart, which is exactly the
 * case where a dispenser most needs the warning.
 */
const NON_CHART = new Map([
    ['cf', 1.9], // counting fingers
    ['hm', 2.3], // hand movements
    ['lp', 2.7], // light perception
    ['nlp', 3.0], // no light perception
]);
/**
 * Convert a recorded acuity to LogMAR.
 *
 * Throws rather than returning a sentinel: a VA that cannot be read is not "average vision", and a
 * silent fallback would make a dispense warning fire or not fire on a typo.
 */
export function toLogMar(acuity) {
    const raw = acuity.value.trim().toLowerCase();
    if (raw === '')
        throw new InvalidVisualAcuity('Empty visual acuity');
    const nonChart = NON_CHART.get(raw.replace(/[\s.]/g, ''));
    if (nonChart !== undefined)
        return nonChart;
    if (acuity.notation === 'logmar') {
        const value = Number(raw);
        if (!Number.isFinite(value))
            throw new InvalidVisualAcuity(`Not a LogMAR value: ${acuity.value}`);
        return value;
    }
    const match = /^(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)$/.exec(raw);
    if (match === null) {
        throw new InvalidVisualAcuity(`Not a ${acuity.notation} fraction: ${acuity.value}`);
    }
    const numerator = Number(match[1]);
    const denominator = Number(match[2]);
    if (numerator <= 0 || denominator <= 0) {
        throw new InvalidVisualAcuity(`A visual acuity fraction has positive terms: ${acuity.value}`);
    }
    // The reference distance is the notation's own numerator: 6 metres, or 20 feet. Using the
    // recorded numerator rather than a constant means 6/9 taken at 3 metres and written 3/4.5 still
    // converts correctly.
    return Math.log10(denominator / numerator);
}
/**
 * Whether `subject` is worse than `reference`.
 *
 * The direction that §12 item 107 turns on: "Aided visual acuity is worse than unaided. The dispense
 * must warn before the job goes to the lab." Higher LogMAR is worse, which is the opposite of how
 * everyone reads 6/6 against 6/12, and is why this is a named function rather than an inline
 * comparison somebody will eventually get backwards.
 */
export function isWorseThan(subject, reference) {
    return toLogMar(subject) > toLogMar(reference);
}
/**
 * §12 item 107: aided acuity worse than unaided means the correction made vision worse, and the job
 * must not reach the lab without someone seeing that.
 *
 * Returns false when either measurement is absent. A missing VA is not evidence of anything, and a
 * warning raised on absence would be ignored within a week.
 */
export function aidedIsWorseThanUnaided(unaided, aided) {
    if (unaided === undefined || aided === undefined)
        return false;
    return isWorseThan(aided, unaided);
}
/** Whether two acuities are the same vision, whichever notation each was written in. */
export function isSameAcuity(a, b) {
    // Rounded to two places: 6/6 and 20/20 are exactly equal, but 6/7.5 and 20/25 differ in the
    // fifteenth decimal place, and a chart line is not measured to that.
    return Math.abs(toLogMar(a) - toLogMar(b)) < 0.005;
}
//# sourceMappingURL=visual-acuity.js.map